import { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import SearchFilterPanel, { describeQuery } from "./SearchFilterPanel";
import { useLocationSuggestions, MIN_CHARS } from "./useLocationSuggestions";
import { useLocationLabels } from "./useLocationLabels";
import {
  knownLocationLabels, locationLabel, rememberLocationLabels,
} from "@suresend/shared/connect/locationLabels";
import { HOME_TYPES } from "@suresend/shared/connect/searchFilters";
import {
  fetchFavorites,
  addFavorite,
  removeFavorite,
  openSignIn,
  stashPendingFavorite,
} from "../favorites/api";
import { connectBase, connectPath } from "../base_path";
import { DEFAULT_TILE_URL, removeLeafletPrefix, tileAttribution } from "../map_attribution";

// SUR-1868 — Custom Search map island. Server-rendered results are the no-JS
// fallback (in the Liquid template, a sibling of this island's mount point);
// this island progressively enhances with an interactive Leaflet map: location
// search + the full advanced filter panel, all POSTing the canonical ez
// `query` object to /_search. Props arrive snake_case unchanged.
//
// props.initial_json is DOUBLE-encoded (drop emits a JSON string, connect_island
// runs props.to_json, islands/index.js JSON.parses once) — so we parse it again.

// CDN marker images (jsdelivr is CSP-allowlisted), as a PLAIN L.icon options
// hash (NOT mergeOptions onto L.Icon.Default, which would prepend the
// auto-detected imagePath and corrupt the full URLs). The search map's own pins
// are price-pill divIcons (buildPin below); this classic pin remains for the
// listing-detail Stimulus map (connect_listing_map_controller).
const CDN = "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images";
export const MARKER_ICON_OPTIONS = {
  iconUrl: `${CDN}/marker-icon.png`,
  iconRetinaUrl: `${CDN}/marker-icon-2x.png`,
  shadowUrl: `${CDN}/marker-shadow.png`,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
};

export function parseInitial(json) {
  if (Array.isArray(json)) return json;
  try {
    const value = JSON.parse(json || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

// The canonical initial ez query the server seeded the SSR results with (e.g.
// {search:["City:Austin, TX"]}). Mirrors parseInitial's double-decode tolerance.
// Without this the island would start from {} and the first sort/filter/paginate
// would POST an empty query — dropping the area scope and broadening to the
// agent's whole tenant feed (and saving an unscoped search).
export function parseInitialQuery(json) {
  if (json && typeof json === "object" && !Array.isArray(json)) return json;
  try {
    const value = JSON.parse(json || "{}");
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
}

// The location text to show for a seeded `search` query, so the input matches
// the active location chip instead of sitting empty. "City:Austin, TX" → "Austin, TX".
export function locationLabelFor(query) {
  const loc = Array.isArray(query?.search) ? query.search[0] : null;
  return loc ? locationLabel(loc) : "";
}

// Every location token in a query — the ones its chips label.
export function queryLocationTokens(query) {
  return [
    ...(Array.isArray(query?.search) ? query.search : []),
    ...(Array.isArray(query?.secondary_locations) ? query.secondary_locations : []),
  ];
}

// Encode a free-text location into an ez `search` string: "78701" → PostalCode,
// "Austin, TX" → City with state, anything else → City.
export function encodeLocation(text) {
  const t = (text || "").trim();
  if (!t) return null;
  if (/^\d{5}$/.test(t)) return `PostalCode:${t}`;
  return `City:${t}`;
}

// Persist/restore the visitor's last search so "Back to search" from a listing
// detail page lands them on exactly the results page they left. Result cards and
// the detail page's back link are full-page navigations that re-mount this island
// fresh from the SSR seed, so we stash the last search in sessionStorage — per-tab
// scope that survives the round trip but doesn't leak a stale search into a brand
// new visit. Only the ez `query`, the pagination `page`, the `sort`, and the
// labels of the query's gazetteer location tokens (which don't carry their own
// names — see shared/connect/locationLabels) are kept.
// All access is guarded — sessionStorage throws when storage is disabled (e.g.
// some privacy modes).
export const LAST_SEARCH_KEY = `ssc:last-search${connectBase() ? `:${connectBase()}` : ""}`;

export function readLastSearch() {
  try {
    const raw = window.sessionStorage.getItem(LAST_SEARCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const query = parsed?.query;
    if (!query || typeof query !== "object" || Array.isArray(query)) return null;
    const page = Number(parsed.page);
    const labels = parsed.labels;
    return {
      query,
      page: Number.isFinite(page) && page >= 1 ? page : 1,
      sort: typeof parsed.sort === "string" ? parsed.sort : "age-desc",
      labels: labels && typeof labels === "object" && !Array.isArray(labels) ? labels : {},
    };
  } catch {
    return null;
  }
}

export function writeLastSearch(state) {
  try {
    window.sessionStorage.setItem(LAST_SEARCH_KEY, JSON.stringify(state));
  } catch {
    /* ignore — last-search persistence is a best-effort enhancement */
  }
}

function hasCoords(listing) {
  return Number.isFinite(listing.lat) && Number.isFinite(listing.lng);
}

function priceLabel(value) {
  if (typeof value !== "number") return null;
  return `$${value.toLocaleString("en-US")}`;
}

function specsLabel(l) {
  return [
    l.beds ? `${l.beds} bed` : null,
    l.baths ? `${l.baths} bath` : null,
    l.sqft ? `${l.sqft.toLocaleString("en-US")} sq ft` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

// Parse a loose money string ("350k", "$425,000", "1.2m") into a number, for the
// quick Price filter popover. Mirrors the design prototype's parseMoney: a bare
// small number (< 10000) is read as thousands so "350" → $350,000.
export function parseMoney(text) {
  const raw = String(text || "").trim();
  let t = raw.toLowerCase().replace(/[$,\s]/g, "");
  if (!t) return 0;
  let mult = 1;
  if (t.endsWith("m")) { mult = 1e6; t = t.slice(0, -1); }
  else if (t.endsWith("k")) { mult = 1e3; t = t.slice(0, -1); }
  let v = parseFloat(t);
  if (!Number.isFinite(v) || v <= 0) return 0;
  v *= mult;
  // Bare shorthand only: "350" → $350K. Explicit forms ("$5,000", "5000.50",
  // "5k") already said what they meant — don't inflate them.
  if (/^\d{1,4}$/.test(raw)) v *= 1000;
  return Math.round(v);
}

// Compact money for the quick-filter chip/button labels: "$350K", "$1.5M".
export function moneyShort(v) {
  if (!(v > 0)) return null;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  return `$${Math.round(v / 1e3)}K`;
}

export function countRangeLabel(min, max, label) {
  if (min && max) return `${min}–${max} ${label}`;
  if (min) return `${min}+ ${label}`;
  if (max) return `Up to ${max} ${label}`;
  return label;
}

// Inline glyphs — the lean Connect bundle carries no icon font, so the searchbar
// magnifier and the quick-filter chevron are hand-drawn SVGs (same posture as the
// favorite heart above).
function MagnifierIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="ssc-ico">
      <path d="M10 4a6 6 0 104.47 10.03l4.25 4.25 1.28-1.28-4.25-4.25A6 6 0 0010 4zm0 2a4 4 0 110 8 4 4 0 010-8z" />
    </svg>
  );
}
function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="ssc-ico ssc-ico--chevron">
      <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
// The save-search star — the design's leading IconRegularStar, hand-drawn since
// the lean Connect bundle carries no icon font. Filled when the current search is
// already saved (the button flips to the "Search Saved" state), an outline
// otherwise.
function StarIcon({ filled }) {
  const d = "M12 2.6l2.9 5.87 6.48.94-4.69 4.57 1.11 6.45L12 17.9l-5.8 3.05 1.11-6.45-4.69-4.57 6.48-.94L12 2.6z";
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="ssc-ico ssc-ico--star">
      <path d={d} fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

// Order-independent serialization of an ez query so we can tell whether the live
// query is already one of the visitor's saved searches (keys can arrive in a
// different order than they were stored). Drives the "Save search" → "Search
// Saved" button state, mirroring the design prototype's `saved` flag.
export function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
}

// Mirrors Connect::SearchResults::SAFE_IMAGE_URL — defense in depth in case a
// listing object ever reaches the client without the server-side scheme guard.
const SAFE_IMAGE_URL = /^(?:https:\/\/|data:image\/)/;

function safePhoto(l) {
  return typeof l.hero_photo === "string" && SAFE_IMAGE_URL.test(l.hero_photo) ? l.hero_photo : null;
}

// Compact price for the map pin, Zillow-style: "$1.4M", "$345K", "$950".
// null when the listing is unpriced (the pin renders as a small dot instead).
export function pinPriceLabel(value) {
  if (typeof value !== "number" || !(value > 0)) return null;
  const k = Math.round(value / 1e3);
  if (k >= 1000) return `$${(value / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1e3) return `$${k}K`;
  return `$${Math.round(value)}`;
}

// Zillow-style map pin: a price pill with a photo thumbnail that appears on
// hover. Built as DOM nodes (L.divIcon accepts an HTMLElement) — never an HTML
// string, same XSS posture as buildPopup. The thumbnail's src is only assigned
// on first hover, so a page of pins doesn't preload every listing photo.
export function buildPin(l) {
  const label = pinPriceLabel(l.price);
  const pin = document.createElement("div");
  pin.className = `ssc-pin${label ? "" : " ssc-pin--dot"}`;

  const photo = safePhoto(l);
  if (photo) {
    const img = document.createElement("img");
    img.className = "ssc-pin-thumb";
    img.setAttribute("alt", "");
    pin.addEventListener("mouseenter", () => img.setAttribute("src", photo), { once: true });
    pin.appendChild(img);
  }

  const pill = document.createElement("span");
  pill.className = "ssc-pin-label";
  if (label) pill.textContent = label;
  pin.appendChild(pill);
  return pin;
}

// Fixed-width popup card; minWidth === maxWidth so Leaflet doesn't shrink-wrap
// the content and the photo always fills the card edge-to-edge.
export const POPUP_OPTIONS = { className: "ssc-pop-wrap", minWidth: 240, maxWidth: 240 };

// The heart glyph, as a fresh SVG node per call (the result-grid cards render
// the same path in JSX). DOM-built so it can live inside the DOM-built popup.
function favHeartSvg() {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", "M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z");
  svg.appendChild(path);
  return svg;
}

// Reflect a favorite button's on/off state — mirrors the JSX card's is-active
// class + aria in a form the DOM-built popup heart can reuse on repaint.
export function paintFavButton(btn, active) {
  btn.classList.toggle("is-active", active);
  btn.setAttribute("aria-pressed", active ? "true" : "false");
  btn.setAttribute("aria-label", active ? "Remove from saved homes" : "Save this home");
}

// Marker popup: a mini listing card (photo · price · specs · address), the whole
// card linking to the detail page — matching the result-grid cards instead of
// the old bare text + "View home" link.
//
// Built as DOM nodes via textContent — NEVER an HTML string. `full_address` is
// raw external MLS text, and Leaflet renders a string popup through innerHTML
// (DivOverlay._updateContent), so interpolating it would be a stored-XSS sink.
// `url` is a server-built relative path and `hero_photo` is scheme-guarded, but
// both are still set via the DOM for consistency. Returns null when there's
// nothing to show.
//
// `fav` (optional, SUR-2374) wires the save-this-home heart the result cards
// have onto the popup: { active, onToggle, register }. Only added when the
// listing has a listing_key; the heart stops the click from following the card
// link, and `register` hands the button back so favKeys changes can repaint it.
export function buildPopup(l, fav) {
  const price = priceLabel(l.price);
  const specs = specsLabel(l);
  if (!price && !specs && !l.full_address && !l.url) return null;

  const card = document.createElement(l.url ? "a" : "div");
  card.className = "ssc-pop";
  if (l.url) {
    card.setAttribute("href", connectPath(l.url));
    card.setAttribute("aria-label", `View ${l.full_address || "home"}`);
  }

  if (fav && l.listing_key) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ssc-fav-btn ssc-fav-btn--pop";
    btn.appendChild(favHeartSvg());
    paintFavButton(btn, fav.active);
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation(); // the whole card is the detail-page link
      fav.onToggle();
    });
    card.appendChild(btn);
    fav.register?.(btn);
  }

  const photo = safePhoto(l);
  if (photo) {
    const img = document.createElement("img");
    img.className = "ssc-pop-img";
    img.setAttribute("src", photo);
    img.setAttribute("alt", l.full_address || "Home");
    img.setAttribute("loading", "lazy");
    card.appendChild(img);
  } else {
    const ph = document.createElement("div");
    ph.className = "ssc-pop-img ssc-pop-ph";
    ph.textContent = "No photo";
    card.appendChild(ph);
  }

  const body = document.createElement("div");
  body.className = "ssc-pop-body";
  [
    ["ssc-pop-price", price],
    ["ssc-pop-specs", specs],
    ["ssc-pop-addr", l.full_address],
  ].forEach(([cls, text]) => {
    if (!text) return;
    const line = document.createElement("div");
    line.className = cls;
    line.textContent = text;
    body.appendChild(line);
  });
  card.appendChild(body);
  return card;
}

export default function SearchMapIsland({
  area,
  total: initialTotal,
  pages: initialPages,
  endpoint = "/_search",
  initial_json: initialJson,
  initial_query_json: initialQueryJson,
  // { token: label } for the seed query's gazetteer tokens ("City:<uuid>"),
  // which don't carry their names. Same double-encoding as initial_query_json.
  location_labels_json: locationLabelsJson,
  tile_url: tileUrl,
  // Framed on a landing page (Connect::EmbedsController): no header to sign in
  // through, so the visitor-account features (Save search, hearts, the saved
  // search deep link) are left out, and the last search is neither restored
  // nor stored — the block starts from its own area every time.
  embedded = false,
}) {
  const initialQuery = parseInitialQuery(initialQueryJson);
  // Learn the seeded labels before the first render, so the chips and headline
  // never flash a placeholder.
  useState(() => rememberLocationLabels(parseInitialQuery(locationLabelsJson)));
  const [listings, setListings] = useState(() => parseInitial(initialJson));
  // Seed total/pages from the SSR props so the result count + pager don't
  // regress to "Homes"/no-pager on hydrate (no search runs on mount).
  const [total, setTotal] = useState(() => Number(initialTotal) || 0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(() => Number(initialPages) || 1);
  const [query, setQuery] = useState(initialQuery);
  const [sort, setSort] = useState("age-desc");
  // The location input is only for ADDING a location — the active locations show
  // as chips inside the searchbar pill (describeQuery → loc: chips), so the input
  // starts empty even when the query is seeded with a location. Seeding it here
  // too would render the same location twice (chip + input text).
  const [locationText, setLocationText] = useState("");
  // The unified searchbar searches ALL location types (the old type-narrowing
  // select was dropped in the SUR redesign); kept as a constant so the type-ahead
  // hook's signature is unchanged.
  const [locationType] = useState("");
  // Whether the visitor is actively engaging the primary picker — gates both the
  // type-ahead fetch and the dropdown, so a seeded locationText doesn't fire a
  // request or pop a menu on mount.
  const [primaryActive, setPrimaryActive] = useState(false);
  // Which quick-filter popover (price / beds / baths / type) is open — null when
  // none. The full "All Filters" modal lives in SearchFilterPanel, separate.
  const [openQuick, setOpenQuick] = useState(null);
  const quickRowRef = useRef(null);
  // Draft text for the quick Price popover's min/max inputs (committed to the
  // query on blur / Enter / Done). Seeded from the live query so re-opening shows
  // the current range.
  const [priceMinTxt, setPriceMinTxt] = useState("");
  const [priceMaxTxt, setPriceMaxTxt] = useState("");
  const [countDrafts, setCountDrafts] = useState({
    beds: { min: "", max: "" },
    baths: { min: "", max: "" },
  });
  const [loading, setLoading] = useState(false);
  const [signedIn, setSignedIn] = useState(null); // null = unknown, then bool
  const [saved, setSaved] = useState([]);
  // Deep-link target from an alert CTA (/search?saved_search=<id>). Read once
  // on mount; applied by the effect below once the visitor's searches load.
  const [pendingSavedSearchId] = useState(
    () => (embedded ? null : new URLSearchParams(window.location.search).get("saved_search") || null),
  );
  const appliedDeepLinkRef = useRef(false);
  const [favKeys, setFavKeys] = useState(() => new Set()); // favorited listing_keys (SUR-2374)
  // key -> the state the user last asked for. Merged over the probe's
  // snapshot so a hydration that lands mid-or-post-toggle can't repaint a
  // tapped heart backwards; dropped only when a toggle fails (server truth).
  const favOverridesRef = useRef(new Map());
  const [savedMsg, setSavedMsg] = useState("");
  const [saveOpen, setSaveOpen] = useState(false); // "Name this search" modal
  const [saveName, setSaveName] = useState("");
  // Alert preferences for the save/edit modal: email defaults ON, text OFF,
  // frequency Daily (the agent can retune per search from the CRM).
  const [saveAlertEmail, setSaveAlertEmail] = useState(true);
  const [saveAlertSms, setSaveAlertSms] = useState(false);
  const [saveCadence, setSaveCadence] = useState("daily");
  const [saving, setSaving] = useState(false);
  const saveInputRef = useRef(null);

  // Primary-picker type-ahead (only fetches while the field is active).
  const { suggestions: primarySuggestions, loading: primaryLoading, clear: clearPrimary } = useLocationSuggestions({
    text: primaryActive ? locationText : "",
    locationType,
  });
  // The highlighted suggestion row — reset to 0 on every new result set so Enter
  // selects the top match. Arrow keys move it.
  const [primaryIndex, setPrimaryIndex] = useState(0);
  const primaryBoxRef = useRef(null);
  const primaryListRef = useRef(null);

  // The term is long enough to query — drives showing the menu (with a
  // "Searching…" hint) the moment the visitor types, before results land.
  const primaryQuerying = primaryActive && locationText.trim().length >= MIN_CHARS;
  const primaryMenuOpen = primaryQuerying;

  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const queryRef = useRef(query);
  const sortRef = useRef(sort);
  queryRef.current = query;
  sortRef.current = sort;
  // Popup hearts live in DOM built outside React's render tree, so renderMarkers
  // (deps: []) reads the latest favorite state + toggle handler through refs, and
  // registered heart nodes are repainted when favKeys changes (effect below).
  const favKeysRef = useRef(favKeys);
  favKeysRef.current = favKeys;
  const toggleFavoriteRef = useRef(null);
  const popupHeartsRef = useRef(new Map()); // listing_key -> popup heart button

  const renderMarkers = useCallback((items) => {
    const layer = markerLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    popupHeartsRef.current.clear(); // stale nodes belong to the cleared markers
    items.filter(hasCoords).forEach((l) => {
      // iconSize null → the divIcon anchors its top-left AT the latlng and the
      // .ssc-pin CSS translates itself up to bottom-anchor the pill's tail there.
      const pin = buildPin(l);
      const marker = L.marker([l.lat, l.lng], {
        icon: L.divIcon({ className: "ssc-pin-anchor", html: pin, iconSize: null, popupAnchor: [0, -34] }),
        riseOnHover: true,
      });
      const popup = buildPopup(
        l,
        l.listing_key && !embedded
          ? {
              active: favKeysRef.current.has(l.listing_key),
              onToggle: () => toggleFavoriteRef.current?.(l),
              register: (btn) => popupHeartsRef.current.set(l.listing_key, btn),
            }
          : null,
      );
      if (popup) {
        marker.bindPopup(popup, POPUP_OPTIONS);
        // Highlight the pin while its popup is open (and let the CSS suppress
        // the hover thumbnail — the popup card already shows the photo).
        marker.on("popupopen", () => pin.classList.add("is-active"));
        marker.on("popupclose", () => pin.classList.remove("is-active"));
      }
      marker.addTo(layer);
    });
    const map = mapRef.current;
    if (map && layer.getLayers().length) {
      try {
        map.fitBounds(layer.getBounds().pad(0.2));
      } catch {
        /* getBounds throws on an empty group — ignore */
      }
    }
  }, [embedded]);

  const runSearch = useCallback(
    async ({ query: nextQuery, page: nextPage = 1, scroll = true } = {}) => {
      const q = nextQuery || queryRef.current;
      setLoading(true);
      try {
        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: q, sort: sortRef.current, page: nextPage }),
          // same-origin (NOT omit): the POST must carry cookies so it reaches the
          // app on gated environments (the staging HTTP Basic Auth middleware
          // requires the signed `staging_auth` cookie on every non-excluded path,
          // and /_search is not excluded — omitting cookies there returns a
          // text/plain 401, res.json() throws, and the search silently no-ops).
          // It also lets the server attribute a registered visitor's search to
          // their lead timeline (Connect::SearchController#record_search_event
          // reads connect_current_person from the session cookie — SUR-1888).
          credentials: "same-origin",
        });
        const data = await res.json();
        const items = Array.isArray(data.listings) ? data.listings : [];
        const resolvedPage = typeof data.page === "number" ? data.page : nextPage;
        setListings(items);
        setTotal(typeof data.total === "number" ? data.total : items.length);
        setPage(resolvedPage);
        setPages(typeof data.pages === "number" ? data.pages : (items.length ? 1 : 0));
        renderMarkers(items);
        // Remember this search (query + page + sort) so "Back to search" from a
        // listing detail restores it. Persisting here covers every entry point —
        // location, filters, sort, pagination, chip removal, and loading a saved
        // search.
        if (!embedded) {
          writeLastSearch({
            query: q, page: resolvedPage, sort: sortRef.current, labels: knownLocationLabels(queryLocationTokens(q)),
          });
        }
        // Scroll the results back into view on a page change.
        if (scroll && mapElRef.current) mapElRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch {
        /* keep the current results on a transient failure */
      } finally {
        setLoading(false);
      }
    },
    [endpoint, renderMarkers, embedded],
  );

  // Mount the Leaflet map once.
  useEffect(() => {
    if (!mapElRef.current || mapRef.current) return undefined;

    const map = L.map(mapElRef.current, { scrollWheelZoom: false });
    mapRef.current = map;
    // Leaflet's own flag + link go (BSD-2-Clause, notice kept in
    // docs/third-party-notices.md); the tile provider's credit stays.
    removeLeafletPrefix(map);
    L.tileLayer(tileUrl || DEFAULT_TILE_URL, {
      maxZoom: 19,
      attribution: tileAttribution(),
    }).addTo(map);

    const markerLayer = L.featureGroup().addTo(map);
    markerLayerRef.current = markerLayer;

    const initial = parseInitial(initialJson);
    renderMarkers(initial);
    if (!initial.some(hasCoords)) map.setView([39.5, -98.35], 4); // continental US

    return () => {
      map.remove();
      mapRef.current = null;
      markerLayerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Probe sign-in + load the visitor's saved searches (needs the session cookie).
  const loadSaved = useCallback(async () => {
    try {
      const res = await fetch("/account/saved_searches", { credentials: "same-origin" });
      if (res.status === 401) {
        setSignedIn(false);
        return;
      }
      setSignedIn(true);
      const data = await res.json();
      setSaved(Array.isArray(data.saved_searches) ? data.saved_searches : []);
    } catch {
      /* ignore — saved searches are optional enhancement */
    }
  }, []);

  useEffect(() => {
    if (!embedded) loadSaved();
  }, [loadSaved, embedded]);

  // Hydrate the visitor's favorited keys (SUR-2374). Also settles signedIn —
  // /account/favorites 401s exactly like /account/saved_searches. The pending
  // (pre-sign-in) favorite is consumed by the body-level connect-favorites
  // Stimulus controller, never here — two consumers would double-post.
  const loadFavorites = useCallback(async () => {
    const state = await fetchFavorites();
    if (!state) return; // network hiccup — hearts stay as they are
    setSignedIn(state.signedIn);
    setFavKeys(() => {
      // The probe's snapshot predates any toggle that raced it — re-apply
      // those flips instead of undoing them.
      const next = new Set(state.keys);
      favOverridesRef.current.forEach((desired, key) => {
        if (desired) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  }, []);

  useEffect(() => {
    if (!embedded) loadFavorites();
  }, [loadFavorites, embedded]);

  // The body-level connect-favorites Stimulus controller replays the pending
  // (pre-sign-in) favorite — never this island; two consumers would
  // double-post. Its POST can land after our probe already hydrated favKeys,
  // so it announces the replay and we fold the key in.
  useEffect(() => {
    const onReplayed = (event) => {
      const key = event.detail?.key;
      if (!key) return;
      setSignedIn(true);
      setFavKeys((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
    };
    window.addEventListener("connect:favorite-replayed", onReplayed);
    return () => window.removeEventListener("connect:favorite-replayed", onReplayed);
  }, []);

  // Display snapshot stored with the favorite (favorite lists / timeline).
  const favoriteSnapshot = (l) => {
    const listing = {
      address: l.full_address,
      price: l.price != null ? String(l.price) : null,
      mls_number: l.mls_number,
      url: l.url,
      hero_photo: l.hero_photo,
    };
    Object.keys(listing).forEach((k) => { if (!listing[k]) delete listing[k]; });
    return Object.keys(listing).length ? listing : null;
  };

  // One mutation in flight per key, re-issued until the server matches the
  // user's latest desired state. Rapid re-taps must not fire concurrent
  // POST/DELETE for the same key — they can execute out of order server-side,
  // leaving the server favorited while the heart shows unfavorited.
  const favSyncingRef = useRef(new Set());
  const syncFavorite = async (key, l) => {
    if (favSyncingRef.current.has(key)) return; // the active loop reconciles new taps
    favSyncingRef.current.add(key);
    try {
      let acked; // the server state this loop has confirmed
      for (;;) {
        const desired = favOverridesRef.current.get(key);
        if (desired === undefined || desired === acked) return;
        const result = desired ? await addFavorite(key, favoriteSnapshot(l)) : await removeFavorite(key);
        if (result === "ok") {
          // Keep the override — a stale probe snapshot must not undo it.
          acked = desired;
          continue;
        }

        // Failure: revert the optimistic flip, back to server truth.
        favOverridesRef.current.delete(key);
        setFavKeys((prev) => {
          const next = new Set(prev);
          if (desired) next.delete(key);
          else next.add(key);
          return next;
        });
        if (result === "unauthorized") {
          setSignedIn(false);
          stashPendingFavorite(key, favoriteSnapshot(l));
          openSignIn();
        }
        return;
      }
    } finally {
      favSyncingRef.current.delete(key);
    }
  };

  // Optimistic heart toggle; revert on failure. Signed-out taps stash the key
  // and open the header sign-in modal (the reload after sign-in replays it).
  const toggleFavorite = (l) => {
    const key = l.listing_key;
    if (!key) return;
    if (signedIn === false) {
      stashPendingFavorite(key, favoriteSnapshot(l));
      openSignIn();
      return;
    }

    const desired = !favKeys.has(key);
    favOverridesRef.current.set(key, desired);
    setFavKeys((prev) => {
      const next = new Set(prev);
      if (desired) next.add(key);
      else next.delete(key);
      return next;
    });
    syncFavorite(key, l);
  };
  // Latest handler for the DOM-built popup hearts (renderMarkers has deps []).
  toggleFavoriteRef.current = toggleFavorite;

  // Repaint the open/registered popup hearts when favKeys changes — hydration,
  // a tap on the grid card, or a replayed pending favorite all flow through here
  // so the map popup and the result card never disagree.
  useEffect(() => {
    popupHeartsRef.current.forEach((btn, key) => paintFavButton(btn, favKeys.has(key)));
  }, [favKeys]);

  // Restore the visitor's last search when they land back on /search — e.g. via
  // "Back to search" from a listing detail page, a full-page navigation that
  // re-mounts this island from the SSR seed. We re-run their last query at the
  // page they left, replacing the seed, so they pick up exactly where they were.
  // Runs once on mount, after the map effect above has wired up the marker layer.
  // We suppress the scroll so a fresh page load isn't yanked past the header; the
  // restored results (and pager) sit in place for the user to scroll to.
  useEffect(() => {
    const last = embedded ? null : readLastSearch();
    if (!last) return;
    rememberLocationLabels(last.labels);
    setQuery(last.query);
    setSort(last.sort);
    sortRef.current = last.sort;
    // Locations surface as chips in the pill (from last.query) — leave the input
    // empty so the restored location isn't shown twice.
    setLocationText("");
    runSearch({ query: last.query, page: last.page, scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Signed-out → openSignIn (shared favorites/api helper) opens the
  // Stimulus-owned sign-in modal in the page header.

  // Open the styled "Name this search" modal (NOT a native window.prompt — it
  // can't be branded, validated, or rendered on the connect page's own theme).
  const saveCurrentSearch = () => {
    setSavedMsg("");
    if (signedIn === false) {
      openSignIn();
      return;
    }
    // Exact query already saved → nothing to do; re-POSTing would create a
    // duplicate alert-firing search (the button reads "Search Saved").
    if (alreadySaved) return;
    setSaveName(area || locationText || "");
    setSaveAlertEmail(true);
    setSaveAlertSms(false);
    setSaveCadence("daily");
    setSaveOpen(true);
  };

  const confirmSaveSearch = async () => {
    const name = saveName.trim();
    if (!name || saving) return;
    setSaving(true);
    setSavedMsg("");
    // No alerts wanted (both channels off) → the search saves with cadence
    // "off"; channels keep their defaults for a later re-enable.
    const wantsAlerts = saveAlertEmail || saveAlertSms;
    const alertFields = {
      alert_cadence: wantsAlerts ? saveCadence : "off",
      alert_via_email: saveAlertEmail,
      alert_via_sms: saveAlertSms,
    };
    try {
      const res = await fetch("/account/saved_searches", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, query, ...alertFields }),
        credentials: "same-origin",
      });
      if (res.status === 401) {
        setSaveOpen(false);
        setSignedIn(false);
        openSignIn();
        return;
      }
      if (!res.ok) {
        setSavedMsg("Couldn’t save — please try again.");
        return;
      }
      setSaveOpen(false);
      setSavedMsg("Saved!");
      loadSaved();
    } catch {
      setSavedMsg("Couldn’t save — please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Close the save modal on Escape; focus the name field when it opens.
  useEffect(() => {
    if (!saveOpen) return undefined;
    saveInputRef.current?.focus();
    const onKey = (e) => { if (e.key === "Escape") setSaveOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [saveOpen]);

  const loadSavedSearch = (s) => {
    const q = s.query || {};
    setQuery(q);
    // Locations render as chips in the searchbar pill (from q) — keep the input
    // empty so a saved location isn't duplicated as both a chip and input text.
    setLocationText("");
    runSearch({ query: q, page: 1 });
  };

  // Deep-link from an alert CTA (/search?saved_search=<id>): open that saved
  // search on the map once the visitor's saved searches have loaded (they need
  // the session cookie). Signed-out → prompt sign-in; the param survives the
  // sign-in reload and applies on the next mount. Applied once, then stripped
  // from the URL so a manual refresh doesn't re-force it.
  useEffect(() => {
    if (!pendingSavedSearchId || appliedDeepLinkRef.current) return;
    if (signedIn === false) {
      openSignIn();
      return;
    }
    if (!saved.length) return; // wait for loadSaved()
    const match = saved.find((s) => String(s.id) === String(pendingSavedSearchId));
    if (!match) return;
    appliedDeepLinkRef.current = true;
    loadSavedSearch(match);
    const url = new URL(window.location.href);
    url.searchParams.delete("saved_search");
    window.history.replaceState({}, "", url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSavedSearchId, signedIn, saved]);

  // Add a primary location (a canonical "Type:Value" id) to the `search` array,
  // de-duped, then re-run. Clears the input + dropdown so the field is ready for
  // the next location (multi-select, ez-parity).
  // A picked suggestion: keep its text (a gazetteer id, "City:<uuid>", has no
  // name in it), then add its id.
  const pickLocation = (suggestion) => {
    rememberLocationLabels([suggestion]);
    addLocation(suggestion?.id);
  };

  const addLocation = (id) => {
    if (!id) return;
    const existing = Array.isArray(query.search) ? query.search : [];
    setLocationText("");
    setPrimaryActive(false);
    clearPrimary();
    if (existing.includes(id)) return;
    const next = { ...query, search: [...existing, id] };
    setQuery(next);
    runSearch({ query: next, page: 1 });
  };

  const onLocationSubmit = (event) => {
    event.preventDefault();
    setPrimaryActive(false);
    clearPrimary();
    const loc = encodeLocation(locationText);
    if (loc) {
      addLocation(loc);
      return;
    }
    // Empty input — the active locations live as chips (removed via their own ×
    // buttons), so hitting Search with a blank field just re-runs the current
    // query. It must NOT drop the `search` scope: clicking Search without typing
    // when a location chip is present would otherwise silently wipe the location
    // and broaden the search tenant-wide.
    setLocationText("");
    runSearch({ query, page: 1 });
  };

  // Close the primary dropdown on an outside click (mousedown).
  useEffect(() => {
    if (!primaryActive) return undefined;
    const onDoc = (e) => {
      if (primaryBoxRef.current && !primaryBoxRef.current.contains(e.target)) setPrimaryActive(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [primaryActive]);

  // Reset the highlight to the top match on every new result set, and keep the
  // highlighted row in view when arrowing through a scrolled list.
  useEffect(() => { setPrimaryIndex(0); }, [primarySuggestions]);
  useEffect(() => {
    if (!primaryMenuOpen) return;
    primaryListRef.current?.children?.[primaryIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [primaryIndex, primaryMenuOpen]);

  // Arrow keys move the highlight; Enter picks the highlighted suggestion
  // (instead of submitting the raw typed text); Escape closes the menu.
  const onPrimaryKeyDown = (e) => {
    if (!primaryMenuOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setPrimaryIndex((i) => Math.min(i + 1, primarySuggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setPrimaryIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (primarySuggestions[primaryIndex]) { e.preventDefault(); pickLocation(primarySuggestions[primaryIndex]); }
    } else if (e.key === "Escape") {
      setPrimaryActive(false);
    }
  };

  const onApplyFilters = (panelQuery) => {
    // The panel preserves the primary `search` scope and may edit secondary
    // city / neighborhood / school refinements.
    setQuery(panelQuery);
    runSearch({ query: panelQuery, page: 1 });
  };

  // ---- Quick-filter popovers (Price / Beds / Baths / Home Type) --------------
  // Each edits the LIVE query directly and re-runs, mirroring onApplyFilters. A
  // patch of { key: value } with value ∈ falsy clears that key (query stays lean,
  // matching Connect::SavedSearchParams' omit-defaults contract).
  const applyQuick = (patch) => {
    const next = { ...query };
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === undefined || v === null || v === false || v === 0) delete next[k];
      else next[k] = v;
    });
    // No-op patches skip the re-run — the Done button's click lands right
    // after the input's blur already committed, and re-POSTing the identical
    // query would double every /_search.
    if (stableStringify(next) === stableStringify(query)) return;
    setQuery(next);
    runSearch({ query: next, page: 1 });
  };

  // "Clear all" in the tags row wipes every FILTER but keeps the location scope
  // (search + secondary_locations) — matching SearchFilterPanel#clearAll, so an
  // empty query never broadens tenant-wide.
  const clearAllFilters = () => {
    const kept = {};
    ["search", "secondary_locations"].forEach((k) => { if (query[k] !== undefined) kept[k] = query[k]; });
    setQuery(kept);
    setPriceMinTxt("");
    setPriceMaxTxt("");
    setCountDrafts({ beds: { min: "", max: "" }, baths: { min: "", max: "" } });
    runSearch({ query: kept, page: 1 });
  };

  const toggleQuick = (menu) => {
    setOpenQuick((cur) => {
      const opening = cur !== menu;
      if (opening && menu === "price") {
        // Seed the price inputs from the live query when the popover opens.
        setPriceMinTxt(query.min_price ? `$${Number(query.min_price).toLocaleString("en-US")}` : "");
        setPriceMaxTxt(query.max_price ? `$${Number(query.max_price).toLocaleString("en-US")}` : "");
      }
      if (opening && (menu === "beds" || menu === "baths")) {
        setCountDrafts((drafts) => ({
          ...drafts,
          [menu]: {
            min: query[`min_${menu}`]?.toString() || "",
            max: query[`max_${menu}`]?.toString() || "",
          },
        }));
      }
      return opening ? menu : null;
    });
  };

  const commitQuickPrice = () => {
    let min = parseMoney(priceMinTxt);
    let max = parseMoney(priceMaxTxt);
    if (min && max && max < min) { const t = min; min = max; max = t; }
    applyQuick({ min_price: min, max_price: max });
    setPriceMinTxt(min ? `$${min.toLocaleString("en-US")}` : "");
    setPriceMaxTxt(max ? `$${max.toLocaleString("en-US")}` : "");
  };

  const setCountDraft = (kind, edge, value) => {
    setCountDrafts((drafts) => ({ ...drafts, [kind]: { ...drafts[kind], [edge]: value } }));
  };

  const commitQuickCount = (kind) => {
    const parse = (value) => {
      const number = Number(value);
      return value === "" || !Number.isFinite(number) || number < 0 ? 0 : Math.floor(number);
    };
    let min = parse(countDrafts[kind].min);
    let max = parse(countDrafts[kind].max);
    if (min && max && max < min) [min, max] = [max, min];
    applyQuick({ [`min_${kind}`]: min, [`max_${kind}`]: max });
    setCountDrafts((drafts) => ({
      ...drafts,
      [kind]: { min: min ? String(min) : "", max: max ? String(max) : "" },
    }));
  };

  // Close any open quick popover on an outside click (mousedown, like the primary
  // picker) or Escape.
  useEffect(() => {
    if (!openQuick) return undefined;
    const onDoc = (e) => {
      if (quickRowRef.current && !quickRowRef.current.contains(e.target)) setOpenQuick(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpenQuick(null); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [openQuick]);

  const onSortChange = (e) => {
    setSort(e.target.value);
    sortRef.current = e.target.value;
    runSearch({ page: 1 });
  };

  const goToPage = (p) => {
    if (p < 1 || p > pages || p === page || loading) return;
    runSearch({ page: p });
  };

  const removeChip = (chip) => {
    const next = chip.remove(query);
    setQuery(next);
    // Removing a chip must not touch the location input — the remaining locations
    // stay visible as their own chips, and the input holds only what the visitor
    // is typing to add a NEW location.
    runSearch({ query: next, page: 1 });
  };

  // The active-filter chips split two ways to match the redesign: LOCATION chips
  // (search + secondary_locations) sit INSIDE the searchbar pill; every other
  // FILTER chip sits in the removable tags row under the filter buttons.
  // Looks up any location token still unlabelled (e.g. from a saved search);
  // re-renders the chips when the labels land.
  useLocationLabels(queryLocationTokens(query));
  const allChips = describeQuery(query);
  const isLocationChip = (c) => c.id.startsWith("loc:") || c.id.startsWith("sec:");
  const locChips = allChips.filter(isLocationChip);
  const filterChips = allChips.filter((c) => !isLocationChip(c));

  // Quick-filter button labels (mirror the active query).
  const priceQuickLabel = query.min_price && query.max_price
    ? `${moneyShort(query.min_price)} – ${moneyShort(query.max_price)}`
    : query.min_price ? `${moneyShort(query.min_price)}+`
      : query.max_price ? `Up to ${moneyShort(query.max_price)}` : "Price";
  const bedsQuickLabel = countRangeLabel(query.min_beds, query.max_beds, "Beds");
  const bathsQuickLabel = countRangeLabel(query.min_baths, query.max_baths, "Baths");
  const activeTypes = HOME_TYPES.filter(([k]) => query[k]);
  const typeQuickLabel = activeTypes.length === 0 ? "Home Type"
    : activeTypes.length === 1 ? activeTypes[0][1] : `${activeTypes.length} Home Types`;

  // The map header block (design: big serif headline + live-count subtitle over
  // the map). One location → "Every Home for Sale in <that place>"; several →
  // "Homes for Sale in N Areas"; none → the server-seeded `area`, else generic.
  const headline = locChips.length > 1
    ? `Homes for Sale in ${locChips.length} Areas`
    : locChips.length === 1
      ? `Every Home for Sale in ${locChips[0].label}`
      : area
        ? `Every Home for Sale in ${area}`
        : "Every Home for Sale";
  const countText = total > 0
    ? `${total.toLocaleString("en-US")} home${total === 1 ? "" : "s"} for sale — the map updates live as you refine your search`
    : "No homes match yet — the map updates live as you refine your search";
  // Already-saved: the live query matches one of the visitor's saved searches
  // (order-independent). Flips the accent button to the design's "Search Saved".
  const currentKey = stableStringify(query);
  const alreadySaved = signedIn === true && saved.some((s) => stableStringify(s.query || {}) === currentKey);
  const saveLabel = signedIn === false ? "Sign in to save" : alreadySaved ? "Search Saved" : "Save search";

  return (
    <div className="ssc-search-island">
      {/* Unified location searchbar — chips + input + Search, in one pill. */}
      <div className="ssc-searchbar-row">
        <div className="ssc-searchbar" ref={primaryBoxRef}>
          <form onSubmit={onLocationSubmit} className="ssc-searchbar-form">
            <div className="ssc-searchbar-fields">
              {locChips.map((chip) => (
                <span key={chip.id} className="ssc-loc-chip">
                  {chip.label}
                  <button type="button" aria-label={`Remove ${chip.label}`} onClick={() => removeChip(chip)}>
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
                  </button>
                </span>
              ))}
              <input type="text" value={locationText} aria-label="Location" className="ssc-searchbar-input"
                     placeholder={locChips.length ? "Add another location" : "City, neighborhood, ZIP, or address"}
                     role="combobox" aria-autocomplete="list" aria-expanded={primaryMenuOpen}
                     onFocus={() => setPrimaryActive(true)}
                     onKeyDown={onPrimaryKeyDown}
                     onChange={(e) => { setPrimaryActive(true); setLocationText(e.target.value); }} />
            </div>
            <button type="submit" className="ssc-searchbar-btn" disabled={loading}>
              {loading ? <span className="ssc-spinner ssc-spinner--light" /> : <><MagnifierIcon />Search</>}
            </button>
          </form>
          {primaryMenuOpen && (
            <ul className="ssc-loc-menu ssc-loc-menu--bar" role="listbox" ref={primaryListRef}>
              <li className="ssc-loc-menu-title" aria-hidden="true">{locationText.trim() ? "Matching locations" : "Popular locations"}</li>
              {primarySuggestions.map((s, i) => (
                <li key={s.id} role="option" aria-selected={i === primaryIndex}>
                  <button type="button" className={`ssc-loc-opt${i === primaryIndex ? " is-active" : ""}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onMouseEnter={() => setPrimaryIndex(i)}
                          onClick={() => pickLocation(s)}>
                    <span className="ssc-loc-opt-text">{s.text}</span>
                    {s.category && <span className="ssc-loc-cat">{s.category}</span>}
                  </button>
                </li>
              ))}
              {primarySuggestions.length === 0 && (
                <li className="ssc-loc-note" role="option" aria-disabled="true">
                  {primaryLoading ? "Searching…" : "No matching locations"}
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      {/* Filter row: All Filters modal trigger · quick popovers · sort · save. */}
      <div className="ssc-filter-row" ref={quickRowRef}>
        <span className="ssc-allfilters" onMouseDownCapture={() => setOpenQuick(null)}>
          <SearchFilterPanel value={query} onApply={onApplyFilters} />
        </span>

        {/* Price */}
        <div className="ssc-quick">
          <button type="button" className={`ssc-quick-btn${query.min_price || query.max_price ? " is-active" : ""}`}
                  aria-expanded={openQuick === "price"} onClick={() => toggleQuick("price")}>
            {priceQuickLabel}<ChevronIcon />
          </button>
          {openQuick === "price" && (
            <div className="ssc-quick-pop ssc-quick-pop--wide">
              <div className="ssc-quick-title">Price range</div>
              <div className="ssc-quick-range">
                <input className="ssc-field" aria-label="Minimum price" value={priceMinTxt} placeholder="No min"
                       onChange={(e) => setPriceMinTxt(e.target.value)} onBlur={commitQuickPrice}
                       onKeyDown={(e) => { if (e.key === "Enter") commitQuickPrice(); }} />
                <span className="ssc-quick-dash">–</span>
                <input className="ssc-field" aria-label="Maximum price" value={priceMaxTxt} placeholder="No max"
                       onChange={(e) => setPriceMaxTxt(e.target.value)} onBlur={commitQuickPrice}
                       onKeyDown={(e) => { if (e.key === "Enter") commitQuickPrice(); }} />
              </div>
              <div className="ssc-quick-hint">Type any amount — e.g. 350k or $425,000</div>
              <div className="ssc-quick-foot">
                <button type="button" className="ssc-quick-done" onClick={() => { commitQuickPrice(); setOpenQuick(null); }}>Done</button>
              </div>
            </div>
          )}
        </div>

        {/* Beds */}
        <div className="ssc-quick">
          <button type="button" className={`ssc-quick-btn${query.min_beds || query.max_beds ? " is-active" : ""}`}
                  aria-expanded={openQuick === "beds"} onClick={() => toggleQuick("beds")}>
            {bedsQuickLabel}<ChevronIcon />
          </button>
          {openQuick === "beds" && (
            <div className="ssc-quick-pop ssc-quick-pop--wide">
              <div className="ssc-quick-title">Bedroom range</div>
              <div className="ssc-quick-range">
                <input type="number" min="0" step="1" inputMode="numeric" className="ssc-field"
                       aria-label="Minimum beds" placeholder="No min" value={countDrafts.beds.min}
                       onChange={(e) => setCountDraft("beds", "min", e.target.value)} />
                <span className="ssc-quick-dash">–</span>
                <input type="number" min="0" step="1" inputMode="numeric" className="ssc-field"
                       aria-label="Maximum beds" placeholder="No max" value={countDrafts.beds.max}
                       onChange={(e) => setCountDraft("beds", "max", e.target.value)} />
              </div>
              <div className="ssc-quick-foot">
                <button type="button" className="ssc-quick-done" onClick={() => { commitQuickCount("beds"); setOpenQuick(null); }}>Done</button>
              </div>
            </div>
          )}
        </div>

        {/* Baths */}
        <div className="ssc-quick">
          <button type="button" className={`ssc-quick-btn${query.min_baths || query.max_baths ? " is-active" : ""}`}
                  aria-expanded={openQuick === "baths"} onClick={() => toggleQuick("baths")}>
            {bathsQuickLabel}<ChevronIcon />
          </button>
          {openQuick === "baths" && (
            <div className="ssc-quick-pop ssc-quick-pop--wide">
              <div className="ssc-quick-title">Bathroom range</div>
              <div className="ssc-quick-range">
                <input type="number" min="0" step="1" inputMode="numeric" className="ssc-field"
                       aria-label="Minimum baths" placeholder="No min" value={countDrafts.baths.min}
                       onChange={(e) => setCountDraft("baths", "min", e.target.value)} />
                <span className="ssc-quick-dash">–</span>
                <input type="number" min="0" step="1" inputMode="numeric" className="ssc-field"
                       aria-label="Maximum baths" placeholder="No max" value={countDrafts.baths.max}
                       onChange={(e) => setCountDraft("baths", "max", e.target.value)} />
              </div>
              <div className="ssc-quick-foot">
                <button type="button" className="ssc-quick-done" onClick={() => { commitQuickCount("baths"); setOpenQuick(null); }}>Done</button>
              </div>
            </div>
          )}
        </div>

        {/* Home type */}
        <div className="ssc-quick">
          <button type="button" className={`ssc-quick-btn${activeTypes.length ? " is-active" : ""}`}
                  aria-expanded={openQuick === "type"} onClick={() => toggleQuick("type")}>
            {typeQuickLabel}<ChevronIcon />
          </button>
          {openQuick === "type" && (
            <div className="ssc-quick-pop ssc-quick-pop--checks">
              {HOME_TYPES.map(([key, label]) => (
                <label key={key} className="ssc-quick-check">
                  <input type="checkbox" checked={!!query[key]} onChange={() => applyQuick({ [key]: !query[key] })} />
                  {label}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* "Name this search" modal (replaces the native window.prompt) */}
      {saveOpen && (
        <div className="ssc-modal" role="dialog" aria-modal="true" aria-label="Save this search"
             onClick={(e) => { if (e.target === e.currentTarget) setSaveOpen(false); }}>
          <div className="ssc-modal-card">
            <div className="ssc-modal-head">
              <h3>Save this search</h3>
              <button type="button" className="ssc-modal-close" aria-label="Close" onClick={() => setSaveOpen(false)}>&times;</button>
            </div>
            <div className="ssc-modal-body">
              <label className="ssc-group">
                <span className="ssc-group-title">Name</span>
                <input ref={saveInputRef} type="text" className="ssc-field" aria-label="Search name"
                       placeholder="e.g. Santa Monica condos under 2M"
                       value={saveName} maxLength={120}
                       onChange={(e) => setSaveName(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmSaveSearch(); } }} />
              </label>
              <div className="ssc-group" style={{ marginTop: "0.75rem" }}>
                <span className="ssc-group-title">Alert me about new matches</span>
                <div style={{ display: "flex", gap: "1.25rem", margin: "0.35rem 0" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.9rem" }}>
                    <input type="checkbox" checked={saveAlertEmail} aria-label="Email alerts"
                           onChange={(e) => setSaveAlertEmail(e.target.checked)} />
                    Email
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.9rem" }}>
                    <input type="checkbox" checked={saveAlertSms} aria-label="Text alerts"
                           onChange={(e) => setSaveAlertSms(e.target.checked)} />
                    Text
                  </label>
                </div>
                <select className="ssc-field" aria-label="Alert frequency" value={saveCadence}
                        disabled={!saveAlertEmail && !saveAlertSms}
                        onChange={(e) => setSaveCadence(e.target.value)}>
                  <option value="realtime">Instant — as soon as a home hits the market</option>
                  <option value="daily">Daily digest</option>
                  <option value="weekly">Weekly digest</option>
                </select>
              </div>
              <p style={{ fontSize: "0.85rem", color: "#6b7783", margin: "0.6rem 0 0" }}>
                {saveAlertEmail || saveAlertSms
                  ? "We’ll let you know when new homes match these filters."
                  : "No alerts — the search is saved for you to run anytime."}
              </p>
            </div>
            <div className="ssc-modal-foot">
              <button type="button" className="ssc-btn" onClick={() => setSaveOpen(false)}>Cancel</button>
              <button type="button" className="ssc-btn ssc-btn--primary" disabled={!saveName.trim() || saving}
                      onClick={confirmSaveSearch}>
                {saving ? "Saving…" : "Save search"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active-filter tags (removable) — locations live in the searchbar pill,
          so only non-location filters surface here. */}
      {filterChips.length > 0 && (
        <div className="ssc-tags">
          {filterChips.map((chip) => (
            <span key={chip.id} className="ssc-tag">
              {chip.label}
              <button type="button" aria-label={`Remove ${chip.label}`} onClick={() => removeChip(chip)}>
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
              </button>
            </span>
          ))}
          <button type="button" className="ssc-tags-clear" onClick={clearAllFilters}>Clear all</button>
        </div>
      )}

      {/* Saved searches are managed on the Account dashboard (remove lives on
          each card there) — the search page keeps only the Save button
          (header, inert once this exact query is saved) + alert-CTA
          deep-linking. */}
      {savedMsg && <p style={{ fontSize: "0.85rem", color: "#8A8A8A", margin: "0.5rem 0 0" }}>{savedMsg}</p>}

      {/* Map header — serif headline + live-count subtitle on the left; the accent
          Save button and Sort control on the right (design: sits ABOVE the map). */}
      <div className="ssc-map-head">
        <div className="ssc-map-head-title">
          {/* h2: both host templates already render the page's h1 in their hero. */}
          <h2>{headline}</h2>
          <div className="ssc-map-head-sub">
            {loading
              ? <span className="ssc-spinner" aria-label="Loading" />
              : <span className="ssc-live-dot" aria-hidden="true" />}
            <span aria-live="polite">{countText}</span>
          </div>
        </div>
        <div className="ssc-map-head-actions">
          {!embedded && (
            <button type="button" className="ssc-btn ssc-btn--accent ssc-save-btn" onClick={saveCurrentSearch} disabled={alreadySaved}>
              <StarIcon filled={alreadySaved} />{saveLabel}
            </button>
          )}
          <label className="ssc-sort">
            <span>Sort</span>
            <select aria-label="Sort" value={sort} onChange={onSortChange} className="ssc-sort-select">
              <option value="age-desc">Newest</option>
              <option value="age-asc">Oldest</option>
              <option value="price-asc">Price ↑</option>
              <option value="price-desc">Price ↓</option>
            </select>
          </label>
        </div>
      </div>

      {/* Map */}
      <div className="ssc-map-wrap">
        <div ref={mapElRef} className="ssc-map" aria-label="Map of homes for sale" />
      </div>

      {/* Result grid heading (the live headline sits above the map). */}
      <div className="ssc-results-head">
        <h2>{total > 0 ? `${total.toLocaleString("en-US")} home${total === 1 ? "" : "s"}` : "Homes"}</h2>
      </div>

      {listings.length === 0 ? (
        <div className="ssc-empty">No homes match these filters yet — try widening your search.</div>
      ) : (
        <div className={`ssc-results${loading ? " is-loading" : ""}`}>
          {listings.map((l, i) => {
            const fav = Boolean(l.listing_key) && favKeys.has(l.listing_key);
            const inner = (
              <>
                <div className="ssc-card-media">
                  {l.hero_photo
                    ? <img className="ssc-card-img" src={l.hero_photo} alt={l.full_address || "Home"} loading="lazy" />
                    : <div className="ssc-card-ph">No photo</div>}
                  {l.listing_key && !embedded && (
                    <button
                      type="button"
                      className={`ssc-fav-btn${fav ? " is-active" : ""}`}
                      aria-pressed={fav}
                      aria-label={fav ? "Remove from saved homes" : "Save this home"}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(l); }}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
                    </button>
                  )}
                </div>
                <div className="ssc-card-body">
                  {priceLabel(l.price) && <div className="ssc-card-price">{priceLabel(l.price)}</div>}
                  {specsLabel(l) && <div className="ssc-card-specs">{specsLabel(l)}</div>}
                  {l.full_address && <div className="ssc-card-addr">{l.full_address}</div>}
                </div>
              </>
            );
            return l.url ? (
              <a key={l.listing_key || i} className="ssc-card" href={connectPath(l.url)} aria-label={`View ${l.full_address || "home"}`}>{inner}</a>
            ) : (
              <div key={l.listing_key || i} className="ssc-card">{inner}</div>
            );
          })}
        </div>
      )}

      {pages > 1 && (
        <nav className="ssc-pager" aria-label="Search results pages">
          <button type="button" className="ssc-btn" onClick={() => goToPage(page - 1)} disabled={page <= 1 || loading}>‹ Prev</button>
          <span style={{ fontSize: "0.9rem", color: "#444" }} aria-live="polite">Page {page} of {pages}</span>
          <button type="button" className="ssc-btn" onClick={() => goToPage(page + 1)} disabled={page >= pages || loading}>Next ›</button>
        </nav>
      )}
    </div>
  );
}
